import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Search as SearchIcon, FileText, Sparkles, Brain, Tag, Zap, Loader2, Globe } from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Search = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [docType, setDocType] = useState('all');
  const [riskLevel, setRiskLevel] = useState('all');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('standard');
  
  // AI Search state
  const [aiQuery, setAiQuery] = useState('');
  const [aiSearchType, setAiSearchType] = useState('semantic');
  const [aiResults, setAiResults] = useState([]);
  const [aiSearchStats, setAiSearchStats] = useState(null);
  const [searchingAI, setSearchingAI] = useState(false);
  const [documentAIEnabled, setDocumentAIEnabled] = useState(false);

  useEffect(() => {
    // Check if Document AI is enabled
    checkDocumentAIStatus();
  }, []);

  const checkDocumentAIStatus = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/document-ai/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentAIEnabled(data.enabled);
      }
    } catch (error) {
      console.log('Document AI status check failed');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setHasSearched(true);

    try {
      const headers = getAuthHeaders();
      const params = {};
      
      if (searchQuery) params.q = searchQuery;
      if (docType !== 'all') params.doc_type = docType;
      if (riskLevel !== 'all') params.risk_level = riskLevel;

      const response = await api.searchDocuments(params, headers);
      setResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAISearch = async (e) => {
    e?.preventDefault();
    
    if (!aiQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setSearchingAI(true);
    setAiResults([]);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/document-ai/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: aiQuery.trim(),
          search_type: aiSearchType,
          limit: 20,
          include_ai_insights: true
        })
      });

      if (!response.ok) throw new Error('AI Search failed');

      const data = await response.json();
      setAiResults(data.results || []);
      setAiSearchStats({
        total: data.total_results || data.results?.length || 0,
        search_time_ms: data.search_time_ms,
        search_type: data.search_type,
        translation: data.translation || null
      });
      setHasSearched(true);

    } catch (error) {
      console.error('AI Search error:', error);
      toast.error('AI Search failed');
    } finally {
      setSearchingAI(false);
    }
  };

  const getSearchTypeIcon = (type) => {
    switch (type) {
      case 'semantic': return Brain;
      case 'entity': return Tag;
      case 'hybrid': return Zap;
      default: return SearchIcon;
    }
  };

  return (
    <div data-testid="search-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Search Documents</h1>
        <p className="text-slate-600 mt-1">Find documents using keywords, filters, or AI-powered search</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="standard" className="flex items-center gap-2">
            <SearchIcon className="h-4 w-4" />
            Standard Search
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2" disabled={!documentAIEnabled}>
            <Sparkles className="h-4 w-4" />
            AI Search
            {!documentAIEnabled && <Badge variant="outline" className="text-xs ml-1">Disabled</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Standard Search Tab */}
        <TabsContent value="standard" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Search Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Search Query</label>
                  <div className="relative">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Enter keywords to search..."
                      data-testid="search-input"
                      className="pr-10"
                    />
                    <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Document Type</label>
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger data-testid="search-doc-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Invoice">Invoice</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Policy">Policy</SelectItem>
                        <SelectItem value="Technical Manual">Technical Manual</SelectItem>
                        <SelectItem value="Report">Report</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Risk Level</label>
                    <Select value={riskLevel} onValueChange={setRiskLevel}>
                      <SelectTrigger data-testid="search-risk-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button type="submit" data-testid="search-submit-button" disabled={searching}>
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Standard Search Results */}
          {hasSearched && activeTab === 'standard' && (
            <Card>
              <CardHeader>
                <CardTitle>Search Results ({results.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {results.length === 0 ? (
                  <div className="text-center py-12">
                    <SearchIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-600">No documents found matching your criteria</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map((doc) => (
                      <div
                        key={doc.id}
                        data-testid={`search-result-${doc.id}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/documents/${doc.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <FileText className="w-8 h-8 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-slate-900">{doc.title}</h3>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-sm text-slate-500">{doc.doc_type}</span>
                              <span className="text-xs text-slate-400">•</span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  doc.risk_level === 'High'
                                    ? 'bg-red-100 text-red-700'
                                    : doc.risk_level === 'Medium'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {doc.risk_level} Risk
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Search Tab */}
        <TabsContent value="ai" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <CardTitle>AI-Powered Search</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Type Toggle */}
              <div className="flex gap-2">
                {[
                  { value: 'semantic', label: 'Semantic', icon: Brain, desc: 'Meaning-based search' },
                  { value: 'entity', label: 'Entities', icon: Tag, desc: 'Search extracted data' },
                  { value: 'hybrid', label: 'Hybrid', icon: Zap, desc: 'Combined search' }
                ].map(({ value, label, icon: Icon, desc }) => (
                  <button
                    key={value}
                    onClick={() => setAiSearchType(value)}
                    className={`flex-1 p-3 rounded-lg border transition-all ${
                      aiSearchType === value 
                        ? 'border-primary bg-primary/5 text-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon className="h-4 w-4 mx-auto mb-1" />
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </button>
                ))}
              </div>

              {/* AI Search Input */}
              <form onSubmit={handleAISearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    placeholder={
                      aiSearchType === 'semantic' ? 'Search by meaning... e.g., "invoices from last month"' :
                      aiSearchType === 'entity' ? 'Search entities... e.g., "vendor:Sharma Electronics"' :
                      'Search documents...'
                    }
                    className="pl-10"
                    disabled={searchingAI}
                    data-testid="ai-search-input"
                  />
                </div>
                <Button type="submit" disabled={searchingAI || !aiQuery.trim()} data-testid="ai-search-submit">
                  {searchingAI ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <SearchIcon className="h-4 w-4" />
                  )}
                </Button>
              </form>

              {/* Search Tips */}
              {!hasSearched && aiResults.length === 0 && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    AI Search Tips
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Semantic:</strong> "Find contracts expiring soon"</li>
                    <li>• <strong>Entity:</strong> "vendor:Sharma Electronics" or "invoice_number:1234"</li>
                    <li>• <strong>Hybrid:</strong> "invoices over ₹10,000 from February"</li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Search Stats */}
          {aiSearchStats && (
            <div className="flex flex-col gap-2 px-1">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{aiSearchStats.total} results</span>
                {aiSearchStats.search_time_ms && (
                  <span>{Math.round(aiSearchStats.search_time_ms)}ms</span>
                )}
                <Badge variant="outline" className="text-xs">
                  {aiSearchStats.search_type || aiSearchType} search
                </Badge>
              </div>
              {aiSearchStats.translation && (
                <div className="flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md w-fit">
                  <Globe className="h-3.5 w-3.5" />
                  <span>
                    Translated from <strong>{aiSearchStats.translation.source_language_name || aiSearchStats.translation.source_language}</strong>: "{aiSearchStats.translation.translated_query}"
                  </span>
                </div>
              )}
            </div>
          )}

          {/* AI Search Results */}
          {activeTab === 'ai' && aiResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>AI Search Results ({aiResults.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {aiResults.map((result, index) => (
                    <div
                      key={result.doc_id || index}
                      data-testid={`ai-search-result-${index}`}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/documents/${result.doc_id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="w-8 h-8 text-primary" />
                        <div>
                          <h3 className="font-semibold text-slate-900">{result.title || 'Untitled'}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge variant="outline">{result.doc_type || 'Document'}</Badge>
                            {result.ai_insights?.classification && (
                              <Badge variant="secondary" className="text-xs">
                                {result.ai_insights.classification.category}
                              </Badge>
                            )}
                            {result.similarity_score && (
                              <span className={`text-xs ${
                                result.similarity_score > 0.8 ? 'text-green-600' : 
                                result.similarity_score > 0.6 ? 'text-yellow-600' : 'text-muted-foreground'
                              }`}>
                                {Math.round(result.similarity_score * 100)}% match
                              </span>
                            )}
                          </div>
                          {result.highlights && result.highlights[0] && (
                            <p className="text-sm text-muted-foreground mt-1 italic">
                              "...{result.highlights[0]}..."
                            </p>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty AI Results */}
          {activeTab === 'ai' && hasSearched && aiResults.length === 0 && !searchingAI && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <Brain className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-600">No AI search results found</p>
                  <p className="text-sm text-muted-foreground mt-1">Try different keywords or search type</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Search;